import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import BaseService from 'services/BaseService'
import { popNotification } from 'utils/popNotification'
import { notificationConfig } from './constants'
import { LIMIT } from 'constants/app.constant'
import { formatDateSend } from '../../functions/formatDateSend'

const { creation, edit, deletion } = notificationConfig

// Redux Toolkit slice
const resultatsSlice = createSlice({
    name: 'resultats',
    initialState: {
        data: [],
        isLoading: false,
        error: null,
        created: null,
        isDeleted: null,
        currentPage: 1,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
        .addCase(fetchDetailsResultats.pending, (state) => {
            state.isLoading = true
            state.error = null
        })
        .addCase(fetchDetailsResultats.fulfilled, (state, action) => {
            const currentPage = action?.meta?.arg?.page || state.currentPage;

            state.currentPage = currentPage
            state.isLoading = false
            state.data = action.payload
        })
        .addCase(fetchDetailsResultats.rejected, (state, action) => {
            state.isLoading = false
            state.error = action.error.message
        })
            .addCase(fetchResultats.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchResultats.fulfilled, (state, action) => {
                const currentPage = action?.meta?.arg?.page || state.currentPage;

                state.currentPage = currentPage
                state.isLoading = false
                state.data = action.payload
            })
            .addCase(fetchResultats.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.error.message
            })
            .addCase(createResultat.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(createResultat.fulfilled, (state, action) => {
                state.isLoading = false
                state.created = true
            })
            .addCase(createResultat.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.error.message
                state.created = false
            })
            .addCase(deleteResultat.pending, (state) => {
                state.isLoading = true
                state.isDeleted = null
            })
            .addCase(deleteResultat.fulfilled, (state) => {
                state.isLoading = false
                state.isDeleted = true
            })
            .addCase(deleteResultat.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.error.message
                state.isDeleted = false
            })
    },
})

// Async thunk action to fetch data from the API
export const fetchResultats = createAsyncThunk(
    'resultats/fetch',
    async (payload) => {
        const page = payload?.page || 1
        const limit = payload?.limit || LIMIT
        const search = payload?.search || ''
        const statut_f = payload?.statut_f || ''
        let date_d_f = payload?.date?.[0] || ''
        let date_f_f = payload?.date?.[1] || ''
        if (dayjs(date_d_f).isValid() && dayjs(date_f_f).isValid()) {
            date_d_f = dayjs(date_d_f).format('YYYY-MM-DD');
            date_f_f = dayjs(date_f_f).format('YYYY-MM-DD');
        } else if (dayjs(date_d_f).isValid()) {
            date_d_f = dayjs(date_d_f).format('YYYY-MM-DD');
            date_f_f = date_d_f;
        } else if (dayjs(date_f_f).isValid()) {
            date_f_f = dayjs(date_f_f).format('YYYY-MM-DD');
            date_d_f = date_f_f;
        }
        try {
            const response = await BaseService.get(
                `/XML/compta.resultat.load?page=${page}&limit=${limit}&text=${search}&statut_f=${statut_f}&date_d_f=${date_d_f}&date_f_f=${date_f_f}`
            )

            return response.data
        } catch (error) {
            throw new Error('Failed to fetch resultats')
        }
    }
)
// Async thunk action to fetch details from the API
export const fetchDetailsResultats = createAsyncThunk(
    'Resultats/fetchDetails',
    async (id) => {
        const data = {'id':id.id,'cminimal':true,'taux_cm':0.25}
        try {
            const exercice = await BaseService.post(
                `/XML/compta.resultat.load/get_exercice`,data
            )
            const journal = await BaseService.post(
                `/XML/compta.jr.load/get_journal_by_lbl`,{'lbl':'R__'+exercice.data[0]['exercice']}
            )
            const cpc = await BaseService.post(
                `/XML/compta.resultat.load/get_cpc_resultat`,data
            )
            

            return {exercice:exercice.data,journal:journal.data,cpc:cpc.data}
        } catch (error) {
            throw new Error('Failed to fetch Resultats')
        }
    }
)
export const createResultat = createAsyncThunk(
    'resultats/post',
    async (payload) => {
        const { data, formik, navigate } = payload
        const { resetForm, setSubmitting } = formik
        if(data?.cminimal == false){
            delete data.cminimal;
        }
     
        try {
            const response = await BaseService.post('/XML/compta.resultat.send/arreteDesComptes', data)
            if (response.status === 200) {
                resetForm()
                setSubmitting(false)
                popNotification(creation?.success)
                navigate('/comptabilite/Resultats')
            }
        } catch (err) {
            console.log({ err })
            
            setSubmitting(false)
            
            popNotification(creation?.error)
        }
    }
)

export const editResultat = createAsyncThunk('resultats/edit', async (payload) => {
    const { data, formik, navigate } = payload
    const { resetForm, setSubmitting } = formik
    try {
        const response = await BaseService.put(`/resultats/${data?.id}`, data)
        if (response.status === 200) {
            resetForm()
            setSubmitting(false)
            popNotification(edit?.success)
            navigate('/resultats')
        }
    } catch (err) {
        console.log({ err })
        
        popNotification(edit?.error)
    }
})

export const deleteResultat = createAsyncThunk(
    'resultat/delete',
    async (payload) => {
        const { id, closeDialog } = payload
        if (!id) return
        try {
            const response = await BaseService.delete(`/resultats/${id}`)
            if (response.status === 200 || response.status === 201) {
                popNotification(deletion?.success)
                closeDialog()
                return true
            }
        } catch (err) {
            console.log({ err })
            popNotification(deletion?.error)
            return
        }
    }
)

export const selectData = (state) => state.resultats.data
export const selectIsLoading = (state) => state.resultats.isLoading
export const selectError = (state) => state.resultats.error
export const selectIsDeleted = (state) => state.resultats.isDeleted
export const selectCurrentPage = (state) => state.resultats.currentPage

export default resultatsSlice.reducer
